/**
 * TaxonomyBatchPanel — Studio header pill that opens a modal to
 *   (1) show a preflight: how many stories still need categorising
 *   (2) offer a one-click "AI auto-suggest for the whole library"
 *   (3) let the creator review + save each suggestion inline
 *
 * Uses /api/admin/taxonomy/stats (preflight) and
 * /api/admin/taxonomy/auto-suggest-all (batch LLM run). Saves via the
 * standard PATCH /api/videos/{id}/taxonomy so any UI updates reflect
 * immediately in the public /stories response.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Layers, X, Sparkles, Loader2, Check, Tag as TagIcon, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "storylo_admin_token";

const _adminToken = () => {
  let t = window.localStorage.getItem(ADMIN_TOKEN_KEY);
  if (!t) {
    t = window.prompt("Enter your admin token:");
    if (t) window.localStorage.setItem(ADMIN_TOKEN_KEY, t);
  }
  return t || "";
};

// Natural title comparator so "06 - Balkans - X" sorts BEFORE
// "07 - Balkans - Y" regardless of upload date. Uses the browser's
// built-in `numeric: true` collation which is exactly what we want.
const _naturalTitle = (a, b) =>
  (a?.title || "").localeCompare(b?.title || "", undefined, {
    numeric: true,
    sensitivity: "base",
  });

export default function TaxonomyBatchPanel() {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState("");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [manifest, setManifest] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStatsErr("");
    axios.get(`${API}/taxonomy`).then(({ data }) => setManifest(data));
    axios
      .get(`${API}/admin/taxonomy/stats`, {
        headers: { "X-Admin-Token": _adminToken() },
      })
      .then(({ data }) => setStats(data))
      .catch((e) => {
        if (e?.response?.status === 403)
          window.localStorage.removeItem(ADMIN_TOKEN_KEY);
        setStatsErr(e?.response?.data?.detail || e.message || "Failed to load stats");
      });
  }, [open]);

  // Escape to close.
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape" && !running) setOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, running]);

  // Live progress. Set to {done, total} while running.
  const [progress, setProgress] = useState(null);

  const runBatch = async () => {
    setRunning(true);
    setResults(null);
    setSavedIds(new Set());
    setStatsErr("");
    setProgress(null);
    try {
      // Fetch the up-to-date eligibility list. We use this instead of
      // the /auto-suggest-all endpoint because that endpoint tries to
      // handle every story in one long HTTP request — on production the
      // Cloudflare timeout kills it before ~30 stories finish. Fanning
      // out per-story calls with client-side concurrency keeps every
      // individual request short and gives the creator live progress.
      const st = await axios.get(`${API}/admin/taxonomy/stats`, {
        headers: { "X-Admin-Token": _adminToken() },
      });
      const eligible = (st.data.eligible_for_autocat || [])
        .slice()
        .sort(_naturalTitle);
      if (eligible.length === 0) {
        setResults({ processed: 0, succeeded: 0, failed: 0, results: [] });
        return;
      }

      setProgress({ done: 0, total: eligible.length });
      const collected = [];
      const CONCURRENCY = 4;
      // Simple concurrency pool: kick off CONCURRENCY workers, each
      // pulling the next id off the queue until empty. Any single-call
      // error becomes a row with status:'failed' — never tanks the run.
      const queue = [...eligible];
      const worker = async () => {
        while (queue.length > 0) {
          const item = queue.shift();
          if (!item) break;
          try {
            const { data } = await axios.post(
              `${API}/admin/taxonomy/auto-suggest/${item.id}`,
              {},
              { headers: { "X-Admin-Token": _adminToken() }, timeout: 45000 }
            );
            collected.push({
              id: item.id, title: item.title,
              status: "ok", suggestion: data.suggestion || {},
            });
          } catch (e) {
            if (e?.response?.status === 403) {
              window.localStorage.removeItem(ADMIN_TOKEN_KEY);
            }
            collected.push({
              id: item.id, title: item.title,
              status: "failed",
              reason: e?.response?.data?.detail || e.message || "unknown error",
            });
          }
          setProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));

      // Preserve the original eligible-list order in the results so the
      // creator sees them in the same order the preflight showed.
      const byId = new Map(collected.map((r) => [r.id, r]));
      const ordered = eligible.map((e) => byId.get(e.id)).filter(Boolean);
      setResults({
        processed: ordered.length,
        succeeded: ordered.filter((r) => r.status === "ok").length,
        failed: ordered.filter((r) => r.status === "failed").length,
        results: ordered,
      });
    } catch (e) {
      if (e?.response?.status === 403)
        window.localStorage.removeItem(ADMIN_TOKEN_KEY);
      setStatsErr(e?.response?.data?.detail || e.message || "Batch failed");
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  const saveOne = async (row) => {
    setSavingId(row.id);
    try {
      await axios.patch(
        `${API}/videos/${row.id}/taxonomy`,
        row.suggestion || {},
        { headers: { "X-Admin-Token": _adminToken() } }
      );
      setSavedIds((s) => {
        const n = new Set(s);
        n.add(row.id);
        return n;
      });
    } catch (e) {
      if (e?.response?.status === 403)
        window.localStorage.removeItem(ADMIN_TOKEN_KEY);
      alert(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const applyAll = async () => {
    if (!results?.results?.length) return;
    if (!window.confirm(`Save AI suggestions for all ${results.succeeded} stories?`)) return;
    for (const row of results.results) {
      if (row.status !== "ok" || savedIds.has(row.id)) continue;
      await saveOne(row);
    }
  };

  const catLabel = (slug) => {
    if (!manifest) return slug;
    const c = manifest.main_categories.find((x) => x.slug === slug);
    return c ? c.label : slug;
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-full border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1.5"
        data-testid="taxonomy-batch-open"
        title="Categorise the whole library with AI suggestions"
      >
        <Layers className="w-3.5 h-3.5" />
        Categorise library
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-8 md:pt-12 pb-8 overflow-y-auto"
          data-testid="taxonomy-batch-modal"
          onClick={(e) => e.target === e.currentTarget && !running && setOpen(false)}
        >
          <div className="panel max-w-3xl w-full max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col my-auto">
            <div className="flex items-start justify-between p-6 border-b border-white/5">
              <div>
                <div className="label-eyebrow mb-1 flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5" /> Categorise library
                </div>
                <div className="font-display text-lg">
                  AI suggestions for every uncategorised story
                </div>
                <div className="text-xs text-[color:var(--muted)] mt-1">
                  Suggestions are drafts — review each one, then save.
                </div>
              </div>
              <button
                onClick={() => !running && setOpen(false)}
                className="text-[color:var(--muted)] hover:text-white"
                data-testid="taxonomy-batch-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {statsErr && (
                <div className="text-xs text-rose-400 border border-rose-500/30 bg-rose-500/10 rounded-lg px-3 py-2">
                  {statsErr}
                </div>
              )}

              {stats && !results && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatBox label="Total published" value={stats.total} />
                  <StatBox label="Categorised" value={stats.with_main_category} tint="emerald" />
                  <StatBox
                    label="Uncategorised"
                    value={stats.total - stats.with_main_category}
                    tint="amber"
                  />
                  <StatBox
                    label="Missing any field"
                    value={stats.missing_any_field}
                    tint="rose"
                  />
                </div>
              )}

              {!results && (
                <div className="panel p-5 flex items-center justify-between gap-4">
                  <div className="text-sm text-[color:var(--muted)] max-w-md">
                    Runs gpt-4o-mini over the title + transcript of each
                    uncategorised story and drafts a full taxonomy row
                    (category, country, year, length, tags, labels).
                    <div className="mt-1 text-[11px] opacity-80">
                      Roughly ~3–5 seconds per story · 4 in parallel.
                    </div>
                  </div>
                  <button
                    onClick={runBatch}
                    disabled={running || !stats}
                    className="btn-primary text-xs inline-flex items-center gap-1.5 px-4 py-2 whitespace-nowrap disabled:opacity-40"
                    data-testid="taxonomy-batch-run"
                  >
                    {running ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        {progress
                          ? `Drafting ${progress.done}/${progress.total}…`
                          : "Running…"}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3.5 h-3.5" /> Draft suggestions
                      </>
                    )}
                  </button>
                </div>
              )}

              {running && progress && progress.total > 0 && (
                <div className="mt-2" data-testid="taxonomy-batch-progress">
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-[color:var(--accent)] transition-all duration-300 ease-out"
                      style={{
                        width: `${Math.round((progress.done / progress.total) * 100)}%`,
                      }}
                    />
                  </div>
                  <div className="text-[11px] text-[color:var(--muted)] mt-1.5">
                    {progress.done} of {progress.total} drafted ·{" "}
                    keep this tab open — you&apos;ll see results as they come in.
                  </div>
                </div>
              )}

              {results && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="text-sm">
                      <span className="text-emerald-400 font-medium">
                        {results.succeeded}
                      </span>{" "}
                      suggestions ready ·{" "}
                      <span className="text-rose-400">{results.failed}</span> failed
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={applyAll}
                        className="text-xs px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                        data-testid="taxonomy-batch-apply-all"
                      >
                        Save all
                      </button>
                      <button
                        onClick={() => {
                          setResults(null);
                          setSavedIds(new Set());
                        }}
                        className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/25"
                      >
                        Run again
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {results.results.map((row) => {
                      const saved = savedIds.has(row.id);
                      const isSaving = savingId === row.id;
                      return (
                        <div
                          key={row.id}
                          className="panel p-3 flex items-start justify-between gap-3"
                          data-testid={`taxonomy-batch-row-${row.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {row.title}
                            </div>
                            {row.status === "ok" ? (
                              <div className="text-[11px] text-[color:var(--muted)] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                {row.suggestion?.main_category && (
                                  <span>
                                    <TagIcon className="w-3 h-3 inline mr-1" />
                                    {catLabel(row.suggestion.main_category)}
                                  </span>
                                )}
                                {row.suggestion?.country && (
                                  <span>📍 {row.suggestion.country}</span>
                                )}
                                {row.suggestion?.year && (
                                  <span>🗓 {row.suggestion.year}</span>
                                )}
                                {row.suggestion?.story_length && (
                                  <span>⏱ {row.suggestion.story_length}</span>
                                )}
                                {(row.suggestion?.tags || []).length > 0 && (
                                  <span>#{(row.suggestion.tags || []).join(" #")}</span>
                                )}
                              </div>
                            ) : (
                              <div className="text-[11px] text-rose-400 mt-1">
                                Failed — {row.reason}
                              </div>
                            )}
                          </div>
                          {row.status === "ok" && (
                            <button
                              onClick={() => saveOne(row)}
                              disabled={isSaving || saved}
                              className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full whitespace-nowrap inline-flex items-center gap-1 ${
                                saved
                                  ? "border border-emerald-500/40 text-emerald-400"
                                  : "border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
                              }`}
                              data-testid={`taxonomy-batch-save-${row.id}`}
                            >
                              {isSaving ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : saved ? (
                                <>
                                  <Check className="w-3 h-3" /> Saved
                                </>
                              ) : (
                                <>
                                  Save <ChevronRight className="w-3 h-3" />
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!stats && !statsErr && (
                <div className="flex items-center justify-center py-12 text-[color:var(--muted)]">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatBox({ label, value, tint }) {
  const color =
    tint === "emerald"
      ? "text-emerald-400"
      : tint === "amber"
      ? "text-amber-300"
      : tint === "rose"
      ? "text-rose-400"
      : "text-white";
  return (
    <div className="panel p-3">
      <div className="label-eyebrow">{label}</div>
      <div className={`font-display text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}
